import IosShareIcon from '@mui/icons-material/IosShare'
import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@rentspree/component-2023.components.atoms.button'
import IconButton from '@rentspree/component-2023.components.atoms.icon-button'
import Tab from '@rentspree/component-2023.components.organisms.tab'
import Tabs from '@rentspree/component-2023.components.organisms.tabs'
import { useEffect, useState } from 'react'

import tracker from 'tracker'
import { LOCATION, MAIN_FEATURE } from 'tracker/events'
import { createCustomTracker } from 'tracker/tracker'

import styles from './index.module.scss'

const HEADER_HEIGHT = 168
interface HeaderProps {
  addressDetails: AddressDetails
  isDisabledShare?: boolean
  isPreview?: boolean
  tabs: {
    label: string
    value: string
    isAvailable: boolean
    ref: React.RefObject<HTMLDivElement>
  }[]
}

const generateShareText = (addressDetails: AddressDetails) => {
  const address = [
    addressDetails.street,
    addressDetails.unitNumber,
    addressDetails.city,
    addressDetails.state,
  ]
    .filter(Boolean)
    .join(', ')
  return `Checkout this rental:\n${address}`
}

export const Header = ({ addressDetails, isDisabledShare, tabs, isPreview }: HeaderProps) => {
  const [activeTab, setActiveTab] = useState(tabs[0].value)
  const [visibleTabs, setVisibleTabs] = useState(new Set())
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    const observers = tabs.map((tab) => {
      const observerCallback = ([entry]: IntersectionObserverEntry[]) => {
        setVisibleTabs((prevSet) => {
          const setOfVisibleTabs = new Set(prevSet)
          if (entry.isIntersecting) {
            setOfVisibleTabs.add(tab.value)
          } else {
            setOfVisibleTabs.delete(tab.value)
          }
          return setOfVisibleTabs
        })
      }

      const observer = new IntersectionObserver(observerCallback, {
        threshold: 0,
        rootMargin: `-${HEADER_HEIGHT}px 0px 0px 0px`,
      })

      if (tab.ref.current) {
        observer.observe(tab.ref.current)
      }
      return observer
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [tabs])

  useEffect(() => {
    const firstVisibleTab = tabs.find((tab) => visibleTabs.has(tab.value))

    if (firstVisibleTab) {
      setActiveTab(firstVisibleTab.value)
    }
  }, [visibleTabs, tabs])

  const handleTabClick = (value: string) => {
    const element = document.getElementById(value)
    if (element && window) {
      const offset = HEADER_HEIGHT
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  const handleShare = () => {
    if (navigator.share && window?.location?.href) {
      tracker?.trackButtonClick({
        click_text: 'Share',
        location: LOCATION.PUBLIC_LISTING,
        screen_experience: isMobile ? 'mobile' : 'desktop',
        main_feature: MAIN_FEATURE.MARKET_LISTING,
      })
      navigator
        .share({
          title: generateShareText(addressDetails),
          url: window.location.href,
        })
        .then(() => {
          createCustomTracker({ userRecorder: true, gtm: true })?.trackEvent('share_success', {
            page_url: window.location.href,
            location: LOCATION.SHARE_SUCCESS,
            screen_experience: isMobile ? 'mobile' : 'desktop',
            main_feature: MAIN_FEATURE.MARKET_LISTING,
          })
        })
        .catch((error) => {
          console.error('Error while sharing:', error)
        })
    }
  }

  return (
    <Box className={`${styles.headerContainer} ${isPreview ? styles.headerPreviewContainer : ''}`}>
      <Box className={styles.headerContent}>
        <Tabs value={activeTab} className={styles.tabsContainer}>
          {tabs.map(
            (tab) =>
              tab.isAvailable && (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                  onClick={() => handleTabClick(tab.value)}
                />
              ),
          )}
        </Tabs>
        {!isDisabledShare && (
          <Box className={styles.shareContainer}>
            {isMobile ? (
              <IconButton onClick={() => handleShare()} color="black" variant="contained">
                <IosShareIcon />
              </IconButton>
            ) : (
              <Button
                variant="text"
                color="secondary"
                className="underline"
                startIcon={<IosShareIcon />}
                onClick={() => handleShare()}
              >
                Share
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
